import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, FolderOpen, Shield, User as UserIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminUsersPage = () => {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [isSuperAdmin, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, projectsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/users`),
        axios.get(`${API_URL}/admin/projects-all`)
      ]);
      setUsers(usersRes.data);
      setAllProjects(projectsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Todos los campos son obligatorios');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/admin/users`, {
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role: 'user'
      });
      toast.success('Usuario creado');
      setCreateDialogOpen(false);
      setNewUser({ name: '', email: '', password: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`¿Eliminar al usuario "${userName}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`);
      toast.success('Usuario eliminado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const openAssignDialog = (user) => {
    setSelectedUser(user);
    setSelectedProjects(user.assigned_projects || []);
    setAssignDialogOpen(true);
  };

  const toggleProject = (projectId) => {
    setSelectedProjects(prev =>
      prev.includes(projectId)
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };

  const handleSaveAssignments = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/users/${selectedUser.id}`, {
        assigned_projects: selectedProjects
      });
      toast.success('Proyectos actualizados');
      setAssignDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar proyectos');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-slate-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight mb-2" data-testid="admin-title">
          Gestión de Usuarios
        </h1>
        <p className="text-sm text-slate-600">Administra usuarios y asigna proyectos</p>
      </div>

      <div className="mb-6">
        <Button
          data-testid="create-user-btn"
          className="bg-blue-600 hover:bg-blue-700"
          onClick={() => setCreateDialogOpen(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Usuario
        </Button>
      </div>

      <div className="space-y-3">
        {users.map(u => (
          <Card key={u.id} data-testid={`user-card-${u.id}`} className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  u.role === 'superadmin' ? 'bg-amber-100' : 'bg-blue-100'
                }`}>
                  {u.role === 'superadmin'
                    ? <Shield className="w-5 h-5 text-amber-600" />
                    : <UserIcon className="w-5 h-5 text-blue-600" />
                  }
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{u.name}</h3>
                    <Badge
                      data-testid={`role-badge-${u.id}`}
                      className={u.role === 'superadmin' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}
                    >
                      {u.role === 'superadmin' ? 'Superadmin' : 'Usuario'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-500">{u.email}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <FolderOpen className="w-3 h-3 text-slate-400" />
                    <span className="text-xs text-slate-500">
                      {u.role === 'superadmin'
                        ? 'Acceso a todos los proyectos'
                        : u.assigned_projects?.length > 0
                          ? `${u.assigned_projects.length} proyecto(s) asignado(s)`
                          : 'Sin proyectos asignados'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {u.role !== 'superadmin' && (
                  <>
                    <Button
                      data-testid={`assign-projects-${u.id}`}
                      variant="outline"
                      size="sm"
                      onClick={() => openAssignDialog(u)}
                    >
                      <FolderOpen className="w-4 h-4 mr-1" />
                      Asignar Proyectos
                    </Button>
                    <Button
                      data-testid={`delete-user-${u.id}`}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteUser(u.id, u.name)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Show assigned project names */}
            {u.role !== 'superadmin' && u.assigned_projects?.length > 0 && (
              <div className="mt-3 pt-3 border-t flex flex-wrap gap-1">
                {u.assigned_projects.map(pid => {
                  const proj = allProjects.find(p => p.id === pid);
                  return (
                    <Badge key={pid} variant="secondary" className="text-xs">
                      {proj?.name || pid.substring(0, 8)}
                    </Badge>
                  );
                })}
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear Nuevo Usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nombre</Label>
              <Input
                data-testid="new-user-name"
                placeholder="Nombre completo"
                value={newUser.name}
                onChange={e => setNewUser({ ...newUser, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                data-testid="new-user-email"
                type="email"
                placeholder="email@ejemplo.com"
                value={newUser.email}
                onChange={e => setNewUser({ ...newUser, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Contraseña</Label>
              <Input
                data-testid="new-user-password"
                type="password"
                placeholder="Contraseña"
                value={newUser.password}
                onChange={e => setNewUser({ ...newUser, password: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancelar</Button>
            <Button
              data-testid="submit-create-user"
              onClick={handleCreateUser}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? 'Creando...' : 'Crear Usuario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Projects Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Asignar Proyectos a {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {allProjects.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No hay proyectos disponibles</p>
            ) : (
              allProjects.map(proj => (
                <div
                  key={proj.id}
                  data-testid={`assign-project-${proj.id}`}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedProjects.includes(proj.id)
                      ? 'bg-blue-50 border-blue-300'
                      : 'hover:bg-slate-50'
                  }`}
                  onClick={() => toggleProject(proj.id)}
                >
                  <Checkbox
                    checked={selectedProjects.includes(proj.id)}
                    onCheckedChange={() => toggleProject(proj.id)}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{proj.name}</p>
                    <p className="text-xs text-slate-500">{proj.description || 'Sin descripción'}</p>
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancelar</Button>
            <Button
              data-testid="save-assignments-btn"
              onClick={handleSaveAssignments}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersPage;
